import React, { useState } from 'react'
import {
  Box,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Typography,
  Button,
} from '@material-ui/core'

import {
  Stepper,
  GeneralInformation,
  OccurrenceScience,
  OpportunityCommunication,
  DescriptionImpact,
  RiskConsequence,
  SecurityMeasure,
} from './components'

import RiskJustificationForm from './components/RiskJustificationForm'

import useIncident from 'hooks/useIncident'
import useIncidentManegement from 'hooks/useIncidentManegement'
import useSnackbar from 'hooks/useSnackbar'
import usePermissions from 'hooks/usePermissions'

import * as service from 'service'
import constants from 'constants/index'
import incidentConstants from 'constants/incidents'
import styles from './styles'

const useStyles = makeStyles(styles)

const Complete = ({ incident, refresh }) => {
  const {
    INCIDENT_INFORMATION_STEPS,
    GENERAL_INFORMATION_STEP,
    SCIENCE_OCCURRENCE_STEP,
    TIMPERTIVITY_COMMUNICATION_STEP,
    DESCRIPTION_IMPACT_STEP,
    RISKS_CONSEQUENCES_STEP,
    SECURITY_MEASURES_STEP,
  } = incidentConstants

  const permissions = usePermissions()
  const classes = useStyles()
  const steps = INCIDENT_INFORMATION_STEPS.map((textStep) => textStep.step)
  const snackbar = useSnackbar()

  const [openRiskJustificationForm, setOpenRiskJustificationForm] =
    useState(false)

  const { lastActiveStep, activeStep, nextStep } = useIncident()
  const {
    handleNext: handleNextIncidentManegementStep,
    isShow,
    resetAdvanceStep,
  } = useIncidentManegement()

  const { INCIDENTS } = constants.permissions
  const testIncidentType =
    incident?.incidentType === constants.documents.TEST_INCIDENT_VALUE
  const canManage =
    (testIncidentType && permissions.permitted(INCIDENTS.CREATE)) ||
    permissions.permitted(INCIDENTS.MANAGE)
  const disabled = isShow || !canManage

  const handleNext = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })

    if (activeStep < 5) {
      if (!disabled) refresh()
      nextStep()
    } else {
      handleNextIncidentManegementStep()
    }
  }

  const handleOpenRiskForm = () => {
    setOpenRiskJustificationForm(true)
  }

  const handleRiskAssumption = async (data) => {
    try {
      if (incident?.riskAssumptions?.detaling?.id) {
        await service.dponet.incidentRiskAssumptions.update({
          incidentId: incident.id,
          id: incident.riskAssumptions.detaling.id,
          incidentRiskAssumption: {
            justification: data.justification,
            attachments: data.attachments,
          },
        })
      } else {
        await service.dponet.incidentRiskAssumptions.create({
          incidentId: incident.id,
          incidentRiskAssumption: {
            justification: data.justification,
            attachments: data.attachments,
            step: constants.incidentRiskAssumption.DETALING_STEP,
          },
        })
      }

      snackbar.open({
        message: 'Risco assumido com sucesso!',
        variant: 'success',
      })

      await service.dponet.incidents.concludeStep({
        incidentId: incident.id,
        step: constants.incidentManegement.DETALING_ENUM,
      })

      setOpenRiskJustificationForm(false)
      refresh()
      resetAdvanceStep()
      handleNextIncidentManegementStep()
    } catch (error) {
      console.error('Erro ao assumir o risco:', error)
      snackbar.open({
        message: 'Erro ao assumir o risco. Tente novamente.',
        variant: 'error',
      })
    }
  }

  return (
    <Grid container>
      <Grid item xl={9} lg={9} md={8} xs={12}>
        {incident?.shouldNotifyAnpd && canManage && (
          <Paper className={classes.fullCardWidth} style={{ marginBottom: 16 }}>
            <Box
              display="flex"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              p={2}
            >
              <Typography variant="subtitle1">
                <Box fontWeight="bold">
                  Assumir o Risco de não comunicar a ANPD mesmo com a orientação
                  do Encarregado de Dados
                </Box>
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleOpenRiskForm}
                disabled={disabled}
              >
                Assumir risco
              </Button>
            </Box>
          </Paper>
        )}

        <Paper className={classes.fullCardWidth}>
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            fontWeight={500}
          >
            <Typography variant="subtitle1">
              <Box fontWeight="bold">
                {INCIDENT_INFORMATION_STEPS[activeStep].title}
              </Box>
            </Typography>
            <Typography variant="subtitle1">
              Etapa {activeStep + 1} de 6
            </Typography>
          </Box>
          <Divider />
          <Grid container>
            <Grid item xs={12}>
              <Box width="100%" mt={2}>
                {activeStep === GENERAL_INFORMATION_STEP && (
                  <GeneralInformation
                    incident={incident}
                    idForm={INCIDENT_INFORMATION_STEPS[activeStep].buttonId}
                    handleNext={handleNext}
                    stepName={INCIDENT_INFORMATION_STEPS[activeStep].title}
                    isShow={disabled}
                  />
                )}
                {activeStep === SCIENCE_OCCURRENCE_STEP && (
                  <OccurrenceScience
                    incident={incident}
                    idForm={INCIDENT_INFORMATION_STEPS[activeStep].buttonId}
                    handleNext={handleNext}
                    stepName={INCIDENT_INFORMATION_STEPS[activeStep].title}
                    isShow={disabled}
                  />
                )}
                {activeStep === TIMPERTIVITY_COMMUNICATION_STEP && (
                  <OpportunityCommunication
                    incident={incident}
                    idForm={INCIDENT_INFORMATION_STEPS[activeStep].buttonId}
                    handleNext={handleNext}
                    stepName={INCIDENT_INFORMATION_STEPS[activeStep].title}
                    isShow={disabled}
                  />
                )}
                {activeStep === DESCRIPTION_IMPACT_STEP && (
                  <DescriptionImpact
                    incident={incident}
                    idForm={INCIDENT_INFORMATION_STEPS[activeStep].buttonId}
                    handleNext={handleNext}
                    stepName={INCIDENT_INFORMATION_STEPS[activeStep].title}
                    isShow={disabled}
                  />
                )}
                {activeStep === RISKS_CONSEQUENCES_STEP && (
                  <RiskConsequence
                    incident={incident}
                    idForm={INCIDENT_INFORMATION_STEPS[activeStep].buttonId}
                    handleNext={handleNext}
                    stepName={INCIDENT_INFORMATION_STEPS[activeStep].title}
                    isShow={disabled}
                  />
                )}
                {activeStep === SECURITY_MEASURES_STEP && (
                  <SecurityMeasure
                    incident={incident}
                    idForm={INCIDENT_INFORMATION_STEPS[activeStep].buttonId}
                    handleNext={handleNext}
                    stepName={INCIDENT_INFORMATION_STEPS[activeStep].title}
                    isShow={disabled}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xl={3} lg={3} md={4} xs={12}>
        <Stepper lastActiveStep={lastActiveStep} steps={steps} />
      </Grid>
      <RiskJustificationForm
        open={openRiskJustificationForm}
        setOpen={setOpenRiskJustificationForm}
        onSubmit={handleRiskAssumption}
        incident={incident}
      />
    </Grid>
  )
}

export default Complete
